import { Component } from 'preact'
import cx from 'classnames'
import { connect } from 'unistore/preact'
import Markdown from 'preact-markdown'

import s from './methods-section.sass'
import gs from '../generic-sign-up.sass'
import CollapsibleSection from '../collapsible-section'
import GridSelect from '../grid-select'
import SelectGroup, { TYPE_RADIO } from '../select-group'
import ArrowSection from '../arrow-section'
import Separator from '../separator'
import { isMobile } from '../../../helpers/devices'
import InputV2 from '../../input-v2'
import Label from '../../label'
import {
  updatePaymentInfo,
  validateAg
} from './actions'
import RowSelect from '../row-select'
import { IconConfirmSolid } from '../../icons/confirm-solid'
import { IconUnconfirmSolid } from '../../icons/unconfirm-solid'
import ErrorList from '../error-list'
import { getFilteredPeriods } from './selectors'
import ActionButton from '../../action-button'
import { PaymentApiError } from '../../../payment-api'
import { checkoutPaymentOption } from '../../../helpers/analytics'
import { replaceNameWithLogo } from '../helpers'
import { PAYMENT_METHODS } from '../constants'

const PeriodSelect = ({ onInput, value, products }) => {
  return (
    <SelectGroup
      className={s.monthList}
      type={TYPE_RADIO}
      onChange={(value) => onInput('periodMonths', parseInt(value))}
      value={value}
    >
      {products && products.map((product, key) => {
        const priceKey = `price${product.paymentMethod[0].toUpperCase() + product.paymentMethod.slice(1)}` // priceCard, priceSwish
        return priceKey
          ? (
            <RowSelect
              key={key}
              className={s.monthItem}
              value={product.periodMonths}
              render={(checked) => [
                checked ? <IconConfirmSolid /> : <IconUnconfirmSolid />,
                <span>{product.periodMonths} månader</span>,
                <span>{product[priceKey].priceSEK || '- '}kr</span>
              ]}
            />
          )
          : null
      })}
    </SelectGroup>
  )
}

export const cardAndSwishSelectors = (state) => {
  const { paymentInfo } = state
  return {
    paymentInfo,
    products: getFilteredPeriods(state)
  }
}

const SwishForm = connect(cardAndSwishSelectors, { updatePaymentInfo })(({
  paymentInfo,
  updatePaymentInfo,
  products,
  handleOnSubmit,
  error
}) => {
  const offset = isMobile()
    ? '50%'
    : 'calc(25% - 0.75rem)'

  const { phone } = (error && error.fields) || {}

  return paymentInfo.paymentMethod === PAYMENT_METHODS.swish && ( // We have to check method once again since preact < 10 cannot guarantee render order.
    <ArrowSection className={s.arrowForm} offset={offset}>
      <form className={s.swishForm} autocomplete='off' onSubmit={(event) => handleOnSubmit(event)}>
        <Label text='Mobilnummer'>
          <InputV2
            type='tel'
            onInput={(e) => updatePaymentInfo('phone', e.target.value)}
            value={paymentInfo.phone}
            error={!!phone}
            messages={phone}
          /></Label>
      </form>
      <PeriodSelect onInput={updatePaymentInfo} value={paymentInfo.periodMonths} products={products} />
    </ArrowSection>
  )
})

const CardForm = connect(cardAndSwishSelectors, { updatePaymentInfo })(({
  paymentInfo,
  updatePaymentInfo,
  products
}) => {
  const offset = isMobile()
    ? '50%'
    : 'calc(75% - 0.75rem)'

  return paymentInfo.paymentMethod === PAYMENT_METHODS.card && ( // We have to check method once again since preact < 10 cannot guarantee render order.
    <ArrowSection className={s.arrowForm} offset={offset}>
      <PeriodSelect onInput={updatePaymentInfo} value={paymentInfo.periodMonths} products={products} />
    </ArrowSection>
  )
})

const AGForm = connect(['paymentInfo'], { updatePaymentInfo })(({
  paymentInfo,
  updatePaymentInfo,
  handleOnSubmit,
  error
}) => {
  const offset = isMobile()
    ? '50%'
    : 'calc(25% - 0.75rem)'

  const { clearingNumber, accountNumber } = (error && error.fields) || {}

  return paymentInfo.paymentMethod === PAYMENT_METHODS.autogiro && ( // We have to check method once again since preact < 10 cannot guarantee render order.
    <ArrowSection className={s.arrowForm} offset={offset}>
      <form className={s.agForm} autocomplete='off' onSubmit={(event) => handleOnSubmit(event)}>
        <Label text='Clearing'>
          <InputV2
            onInput={(e) => updatePaymentInfo('clearingNumber', e.target.value)}
            value={paymentInfo.clearingNumber}
            error={!!clearingNumber}
            messages={clearingNumber}
          />
        </Label>
        <Label text='Kontonummer'>
          <InputV2
            onInput={(e) => updatePaymentInfo('accountNumber', e.target.value)}
            value={paymentInfo.accountNumber}
            error={!!accountNumber}
            messages={accountNumber}
          />
        </Label>
        <input type='submit' hidden />

        <p>Första inbetalningen för perioden fram till nästa månadsskifte gör du nu direkt med betalkort, därefter dras månadskostnaden automatiskt från ditt bankkonto.</p>
      </form>
    </ArrowSection>
  )
})

class MethodsSection extends Component {
  state = {
    methodError: false,
    isValidatingAg: false,
    agError: undefined
  }

  componentDidUpdate (prevProps) {
    if (prevProps.paymentInfo.isRecurring !== this.props.paymentInfo.isRecurring) { // If the user changes plan(isRecurring) reset error
      this.setState({ error: undefined })
    }
  }

  handleOnMethodInput (value) {
    let periodMonths
    if (value === PAYMENT_METHODS.autogiro || value === PAYMENT_METHODS.rcp) {
      const product = this.props.paymentProducts.find(product => product.paymentMethod === value)
      periodMonths = product.periodMonths
    }
    this.setState({
      error: undefined
    }, () => this.props.onInput(value, periodMonths))

    checkoutPaymentOption(value)
  }

  async validate () {
    const { paymentInfo, validateAg } = this.props
    return new Promise((resolve, reject) => {
      switch (paymentInfo.paymentMethod) {
        case PAYMENT_METHODS.autogiro: {
          return validateAg()
            .then(() => {
              resolve()
            })
            .catch((error) => {
              reject(error)
            })
        }

        case PAYMENT_METHODS.rcp: {
          return resolve()
        }

        case PAYMENT_METHODS.swish: {
          if (paymentInfo.phone && paymentInfo.periodMonths) {
            return resolve()
          } else {
            const phone = !paymentInfo.phone && {
              field: 'phone',
              message: 'phone is required',
              error: 'INVALID'
            }
            const periodMonths = !paymentInfo.periodMonths && {
              field: 'periodMonths',
              message: 'periodMonths is required',
              error: 'INVALID'
            }

            const additional = [phone, periodMonths].filter(v => v)

            throw new PaymentApiError({
              code: 'FE',
              additional
            }, 'Du har glömt fylla i formuläret.')
          }
        }

        case PAYMENT_METHODS.card: {
          if (paymentInfo.periodMonths) {
            return resolve()
          } else {
            throw new Error('Du har glömt välja period.')
          }
        }

        default:
          throw new Error('Du måste välja ett betalsätt.')
      }
    })
  }

  proceed (event) {
    event && event.preventDefault()

    this.setState({
      error: undefined,
      isValidating: true
    })

    this.validate()
      .then(() => {
        this.setState({
          error: undefined,
          isValidating: false
        })
        this.props.proceedToNext()
      })
      .catch((error) => {
        console.error(error.fields)
        this.setState({
          error,
          isValidating: false
        })
      })
  }

  render ({
    title,
    description,
    inputs,
    signUpPersonalInfo,
    paymentInfo,
    paymentProducts,
    hasAllMethods,
    proceedToNext,
    onInput,
    ...rest
  }, {
    isValidating,
    error
  }) {
    const combinedClassNames = cx(
      s.methodsSection,
      !hasAllMethods && s.noPadding
    )

    const { paymentMethod } = paymentInfo
    const availableMethods = paymentProducts.reduce((result, current) => [...result, current.paymentMethod], [])
    const filteredInputs = inputs
      .filter(input => availableMethods.includes(input.value))
      .map(replaceNameWithLogo)

    return (
      <CollapsibleSection
        className={combinedClassNames}
        {...rest}
      >
        {hasAllMethods && <Separator className={gs.signUpSeparator} />}
        <div className={s.content}>
          <h2>{title}</h2>
          <div className={s.description}><Markdown markdown={description || ''} /></div>
          <SelectGroup
            className={s.gridSelect}
            style={`--no-inputs: ${filteredInputs.length}`}
            type={TYPE_RADIO}
            onChange={(value) => this.handleOnMethodInput(value)}
            value={paymentMethod}
          >
            {filteredInputs.map((input, key) => <GridSelect key={key} {...input} />)}
          </SelectGroup>
          {paymentInfo.paymentMethod !== null &&
            <div className={s.additionalInputs}>
              {paymentMethod === PAYMENT_METHODS.autogiro &&
                <AGForm handleOnSubmit={(event) => this.proceed(event)} error={this.state.error} />}

              {paymentMethod === PAYMENT_METHODS.swish &&
                <SwishForm handleOnSubmit={(event) => this.proceed(event)} error={this.state.error} />}

              {paymentMethod === PAYMENT_METHODS.card &&
                <CardForm error={this.state.error} />}
            </div>
          }

          <div className={s.proceed}>
            {error && <ErrorList message={error.message} errorFields={error.fields} />}
            <ActionButton className={s.proceedButton} label='Välj och gå vidare' onClick={() => this.proceed()} loading={isValidating} />
          </div>
        </div>
      </CollapsibleSection>
    )
  }
}

export default connect(['signUpPersonalInfo', 'paymentInfo', 'paymentProducts'], { updatePaymentInfo, validateAg })(MethodsSection)



// WEBPACK FOOTER //
// ./components/signup-flow/step-payment/methods-section.js